// ═══════════════════════════════════════════════════════════════════════════
// APEX MARKOV DASHBOARD COMPONENTS
// File: src/components/MarkovDashboard.jsx
// Import in App.js: import { RegimeBadge, SessionPnL, SequenceRiskBar,
//                            KillSwitch, TradeJournalTab, WinRateByRegime,
//                            AlertPanel, MarkovStatusBar } from './MarkovDashboard';
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback } from 'react';

// ── Shared hook — fetches all Markov status every 15 seconds ─────────────────
export function useMarkovStatus() {
  const [status, setStatus]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const refresh = useCallback(() => {
    fetch('/api/markov/journal')
      .then(r => r.json())
      .then(d => { setStatus(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 15000);
    return () => clearInterval(t);
  }, [refresh]);

  return { status, loading, error, refresh };
}

// ── Color helpers ─────────────────────────────────────────────────────────────
const REGIME_COLORS = {
  TRENDING: { bg: '#00c85322', border: '#00c853', text: '#00c853', dot: '#00c853' },
  CHOPPY:   { bg: '#ffd60022', border: '#ffd600', text: '#ffd600', dot: '#ffd600' },
  VOLATILE: { bg: '#ff3d0022', border: '#ff3d00', text: '#ff3d00', dot: '#ff3d00' },
  UNKNOWN:  { bg: '#75757522', border: '#757575', text: '#757575', dot: '#757575' },
};

// ═══════════════════════════════════════════════════════════════════════════
// 1. REGIME BADGE — shows current market regime with pulsing dot
// Usage: <RegimeBadge C={C} />
// ═══════════════════════════════════════════════════════════════════════════
export function RegimeBadge({ C }) {
  const { status, loading } = useMarkovStatus();
  const label  = status?.regime?.label || 'UNKNOWN';
  const colors = REGIME_COLORS[label] || REGIME_COLORS.UNKNOWN;

  const transProbs = status?.regime?.transitionProbs || {};
  const nextBest   = Object.entries(transProbs).sort((a,b) => b[1]-a[1])[0];

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      {/* Pulsing dot */}
      <div style={{
        width: 8, height: 8, borderRadius: '50%',
        background: colors.dot,
        boxShadow: `0 0 6px ${colors.dot}`,
        animation: label === 'TRENDING' ? 'pulse 1.5s infinite' : 'none',
      }} />

      {/* Regime label */}
      <div style={{
        padding: '3px 10px',
        borderRadius: 4,
        border: `1px solid ${colors.border}`,
        background: colors.bg,
        color: colors.text,
        fontSize: 10,
        fontFamily: 'JetBrains Mono',
        fontWeight: 700,
        letterSpacing: 2,
      }}>
        {loading ? 'LOADING...' : `REGIME: ${label}`}
      </div>

      {/* Next state probability */}
      {nextBest && !loading && (
        <div style={{ fontSize: 9, color: C?.muted || '#757575', fontFamily: 'JetBrains Mono' }}>
          → {nextBest[0]} {(nextBest[1]*100).toFixed(0)}%
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// 2. SESSION P&L TICKER — live running P&L for the day
// Usage: <SessionPnL C={C} />
// ═══════════════════════════════════════════════════════════════════════════
export function SessionPnL({ C }) {
  const { status, loading } = useMarkovStatus();
  const summary = status?.todaySummary;
  const pnl     = summary?.total_pnl || 0;
  const trades  = summary?.total_trades || 0;
  const wins    = summary?.wins || 0;
  const losses  = summary?.losses || 0;
  const wr      = summary?.win_rate || 0;

  const color   = pnl > 0 ? (C?.green || '#00c853') : pnl < 0 ? (C?.red || '#ff3d00') : (C?.muted || '#757575');
  const sign    = pnl >= 0 ? '+' : '';

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 16,
      padding: '6px 14px',
      background: C?.panel || '#1a1a1a',
      border: `1px solid ${C?.border || '#2a2a2a'}`,
      borderRadius: 6,
    }}>
      {/* P&L */}
      <div>
        <div style={{ fontSize: 8, color: C?.muted, fontFamily: 'JetBrains Mono', letterSpacing: 1 }}>SESSION P&L</div>
        <div style={{ fontSize: 18, fontWeight: 700, color, fontFamily: 'JetBrains Mono' }}>
          {loading ? '--' : `${sign}$${Math.abs(pnl).toFixed(2)}`}
        </div>
      </div>

      {/* Divider */}
      <div style={{ width: 1, height: 30, background: C?.border }} />

      {/* Win rate */}
      <div>
        <div style={{ fontSize: 8, color: C?.muted, fontFamily: 'JetBrains Mono', letterSpacing: 1 }}>WIN RATE</div>
        <div style={{ fontSize: 16, fontWeight: 700, color: wr >= 75 ? (C?.green) : wr >= 60 ? (C?.yellow) : (C?.red), fontFamily: 'JetBrains Mono' }}>
          {loading ? '--' : `${wr.toFixed(0)}%`}
        </div>
      </div>

      {/* Divider */}
      <div style={{ width: 1, height: 30, background: C?.border }} />

      {/* Trade count */}
      <div>
        <div style={{ fontSize: 8, color: C?.muted, fontFamily: 'JetBrains Mono', letterSpacing: 1 }}>TRADES</div>
        <div style={{ fontSize: 14, fontWeight: 700, color: C?.text, fontFamily: 'JetBrains Mono' }}>
          <span style={{ color: C?.green }}>{wins}W</span>
          <span style={{ color: C?.muted }}> / </span>
          <span style={{ color: C?.red }}>{losses}L</span>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// 3. SEQUENCE RISK BAR — visual loss streak indicator
// Usage: <SequenceRiskBar C={C} />
// ═══════════════════════════════════════════════════════════════════════════
export function SequenceRiskBar({ C }) {
  const { status, loading } = useMarkovStatus();
  const seq     = status?.sequence || {};
  const losses  = seq.consecutiveLosses || 0;
  const wins    = seq.consecutiveWins   || 0;
  const paused  = seq.isPaused || false;

  const levels = [
    { label: '0L', desc: 'FULL SIZE', color: C?.green || '#00c853',  active: losses === 0 && !paused },
    { label: '1L', desc: '75% SIZE',  color: C?.yellow || '#ffd600', active: losses === 1 },
    { label: '2L', desc: '50% SIZE',  color: '#ff9100',              active: losses === 2 },
    { label: '3L', desc: 'PAUSED',    color: C?.red || '#ff3d00',    active: losses >= 3 || paused },
  ];

  const currentLevel = paused ? 3 : Math.min(losses, 3);
  const currentColor = levels[currentLevel]?.color;

  return (
    <div style={{
      padding: '8px 14px',
      background: C?.panel || '#1a1a1a',
      border: `1px solid ${paused ? (C?.red || '#ff3d00') : (C?.border || '#2a2a2a')}`,
      borderRadius: 6,
    }}>
      <div style={{ fontSize: 8, color: C?.muted, fontFamily: 'JetBrains Mono', letterSpacing: 1, marginBottom: 6 }}>
        SEQUENCE RISK
      </div>

      {/* Progress bar */}
      <div style={{ display: 'flex', gap: 3, marginBottom: 4 }}>
        {levels.map((l, i) => (
          <div key={i} style={{
            flex: 1, height: 4, borderRadius: 2,
            background: i <= currentLevel ? l.color : (C?.border || '#2a2a2a'),
            transition: 'background 0.3s',
          }} />
        ))}
      </div>

      {/* Labels */}
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        {levels.map((l, i) => (
          <div key={i} style={{
            fontSize: 8,
            fontFamily: 'JetBrains Mono',
            color: i === currentLevel ? currentColor : (C?.muted || '#757575'),
            fontWeight: i === currentLevel ? 700 : 400,
          }}>
            {l.desc}
          </div>
        ))}
      </div>

      {/* Status line */}
      <div style={{ marginTop: 4, fontSize: 9, color: currentColor, fontFamily: 'JetBrains Mono' }}>
        {loading ? 'LOADING...' :
         paused  ? `⛔ PAUSED — ${seq.pauseReason || ''}` :
         losses === 0 && wins > 0 ? `✅ ${wins} WIN STREAK` :
         losses === 0 ? '✅ CLEAN SLATE' :
         `⚠️ ${losses} CONSECUTIVE LOSS${losses > 1 ? 'ES' : ''}`}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// 4. KILL SWITCH — emergency stop button
// Usage: <KillSwitch C={C} />
// ═══════════════════════════════════════════════════════════════════════════
export function KillSwitch({ C }) {
  const [status, setStatus]   = useState('idle'); // idle | killing | killed | error | resuming
  const [message, setMessage] = useState('');
  const { status: mStatus, refresh } = useMarkovStatus();
  const botOnline = mStatus?.botStatus?.running;

  const handleKill = async () => {
    if (!window.confirm('⛔ EMERGENCY STOP\n\nThis will immediately stop the trading bot.\nAll open positions will remain open.\n\nConfirm?')) return;
    setStatus('killing');
    try {
      const r = await fetch('/api/markov/kill', { method: 'POST' }).then(r => r.json());
      if (r.ok) { setStatus('killed'); setMessage('Bot stopped'); }
      else       { setStatus('error');  setMessage(r.error); }
    } catch(e)   { setStatus('error');  setMessage(e.message); }
    setTimeout(refresh, 2000);
  };

  const handleResume = async () => {
    setStatus('resuming');
    try {
      const r = await fetch('/api/markov/resume', { method: 'POST' }).then(r => r.json());
      if (r.ok) { setStatus('idle'); setMessage(''); }
      else       { setStatus('error'); setMessage(r.error); }
    } catch(e)   { setStatus('error'); setMessage(e.message); }
    setTimeout(refresh, 2000);
  };

  const isKilling  = status === 'killing'  || status === 'resuming';
  const isKilled   = status === 'killed';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      {!isKilled ? (
        <button
          onClick={handleKill}
          disabled={isKilling}
          style={{
            padding: '6px 14px',
            borderRadius: 4,
            border: `1px solid ${C?.red || '#ff3d00'}`,
            background: isKilling ? 'transparent' : `${C?.red || '#ff3d00'}22`,
            color: C?.red || '#ff3d00',
            fontSize: 10,
            fontFamily: 'JetBrains Mono',
            fontWeight: 700,
            letterSpacing: 1,
            cursor: isKilling ? 'not-allowed' : 'pointer',
          }}
        >
          {isKilling ? 'STOPPING...' : '⛔ KILL BOT'}
        </button>
      ) : (
        <button
          onClick={handleResume}
          disabled={isKilling}
          style={{
            padding: '6px 14px',
            borderRadius: 4,
            border: `1px solid ${C?.green || '#00c853'}`,
            background: `${C?.green || '#00c853'}22`,
            color: C?.green || '#00c853',
            fontSize: 10,
            fontFamily: 'JetBrains Mono',
            fontWeight: 700,
            letterSpacing: 1,
            cursor: 'pointer',
          }}
        >
          ▶ RESUME BOT
        </button>
      )}

      {message && (
        <div style={{ fontSize: 9, color: status === 'error' ? C?.red : C?.green, fontFamily: 'JetBrains Mono' }}>
          {message}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// 5. TRADE JOURNAL TAB — full trade history with regime context
// Usage: <TradeJournalTab C={C} />
// ═══════════════════════════════════════════════════════════════════════════
export function TradeJournalTab({ C }) {
  const [trades, setTrades]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [filter, setFilter]     = useState({ regime: '', date: '' });
  const [page, setPage]         = useState(0);
  const PER_PAGE = 20;

  const load = useCallback(() => {
    const params = new URLSearchParams({ limit: PER_PAGE, offset: page * PER_PAGE });
    if (filter.regime) params.set('regime', filter.regime);
    if (filter.date)   params.set('date',   filter.date);
    fetch(`/api/markov/journal?${params}`)
      .then(r => r.json())
      .then(d => { setTrades(d.trades || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [filter, page]);

  useEffect(() => { load(); }, [load]);

  const btn = (label, val, field) => (
    <button
      key={val}
      onClick={() => setFilter(f => ({ ...f, [field]: f[field] === val ? '' : val }))}
      style={{
        padding: '3px 8px', borderRadius: 4, fontSize: 9,
        fontFamily: 'JetBrains Mono', cursor: 'pointer',
        border: `1px solid ${filter[field] === val ? (C?.accent || '#f5f5f5') : (C?.border || '#2a2a2a')}`,
        background: filter[field] === val ? `${C?.accent || '#f5f5f5'}18` : 'transparent',
        color: filter[field] === val ? (C?.accent || '#f5f5f5') : (C?.muted || '#757575'),
      }}
    >{label}</button>
  );

  return (
    <div>
      {/* Filters */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: 9, color: C?.muted, fontFamily: 'JetBrains Mono' }}>REGIME:</span>
        {['TRENDING', 'CHOPPY', 'VOLATILE'].map(r => btn(r, r, 'regime'))}
        <div style={{ width: 1, height: 16, background: C?.border, margin: '0 4px' }} />
        <input
          type="date"
          value={filter.date}
          onChange={e => setFilter(f => ({ ...f, date: e.target.value }))}
          style={{
            padding: '2px 6px', borderRadius: 4, fontSize: 9,
            background: C?.dim, border: `1px solid ${C?.border}`,
            color: C?.text, fontFamily: 'JetBrains Mono',
          }}
        />
        <button onClick={() => setFilter({ regime: '', date: '' })} style={{
          padding: '3px 8px', borderRadius: 4, fontSize: 9, cursor: 'pointer',
          fontFamily: 'JetBrains Mono', border: `1px solid ${C?.border}`,
          background: 'transparent', color: C?.muted,
        }}>CLEAR</button>
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 20, color: C?.muted, fontSize: 11 }}>Loading journal...</div>
      ) : trades.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 20, color: C?.muted, fontSize: 11 }}>
          No trades yet — data builds as you trade
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10, fontFamily: 'JetBrains Mono' }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${C?.border}` }}>
                {['DATE','TICKER','DIR','REGIME','SCORE','ENTRY','EXIT','P&L','RESULT','REASON'].map(h => (
                  <th key={h} style={{ padding: '4px 8px', textAlign: 'left', color: C?.muted, fontWeight: 400, fontSize: 8, letterSpacing: 1 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {trades.map((t, i) => {
                const rc = REGIME_COLORS[t.regime_label] || REGIME_COLORS.UNKNOWN;
                const pnlColor = t.pnl > 0 ? C?.green : C?.red;
                return (
                  <tr key={i} style={{ borderBottom: `1px solid ${C?.border}22`, ':hover': { background: C?.dim } }}>
                    <td style={{ padding: '5px 8px', color: C?.muted }}>{t.date}</td>
                    <td style={{ padding: '5px 8px', color: C?.text, fontWeight: 700 }}>{t.ticker}</td>
                    <td style={{ padding: '5px 8px', color: t.direction === 'BULL' ? C?.green : C?.red }}>{t.direction}</td>
                    <td style={{ padding: '5px 8px' }}>
                      <span style={{ padding: '1px 5px', borderRadius: 3, border: `1px solid ${rc.border}`, color: rc.text, fontSize: 8 }}>
                        {t.regime_label || 'N/A'}
                      </span>
                    </td>
                    <td style={{ padding: '5px 8px', color: C?.text }}>{t.score?.toFixed(0)}</td>
                    <td style={{ padding: '5px 8px', color: C?.muted }}>${t.entry_premium?.toFixed(2)}</td>
                    <td style={{ padding: '5px 8px', color: C?.muted }}>${t.exit_premium?.toFixed(2)}</td>
                    <td style={{ padding: '5px 8px', color: pnlColor, fontWeight: 700 }}>
                      {t.pnl >= 0 ? '+' : ''}${t.pnl?.toFixed(2)}
                    </td>
                    <td style={{ padding: '5px 8px', color: t.result === 'WIN' ? C?.green : C?.red, fontWeight: 700 }}>
                      {t.result}
                    </td>
                    <td style={{ padding: '5px 8px', color: C?.muted, fontSize: 8 }}>{t.exit_reason}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {trades.length > 0 && (
        <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'center' }}>
          <button onClick={() => setPage(p => Math.max(0, p-1))} disabled={page === 0}
            style={{ padding: '3px 10px', borderRadius: 4, fontSize: 9, cursor: 'pointer',
              fontFamily: 'JetBrains Mono', border: `1px solid ${C?.border}`,
              background: 'transparent', color: page === 0 ? C?.muted : C?.text }}>
            PREV
          </button>
          <span style={{ fontSize: 9, color: C?.muted, fontFamily: 'JetBrains Mono', lineHeight: '22px' }}>
            PAGE {page + 1}
          </span>
          <button onClick={() => setPage(p => p+1)} disabled={trades.length < PER_PAGE}
            style={{ padding: '3px 10px', borderRadius: 4, fontSize: 9, cursor: 'pointer',
              fontFamily: 'JetBrains Mono', border: `1px solid ${C?.border}`,
              background: 'transparent', color: trades.length < PER_PAGE ? C?.muted : C?.text }}>
            NEXT
          </button>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// 6. WIN RATE BY REGIME — bar chart showing edge per market state
// Usage: <WinRateByRegime C={C} />
// ═══════════════════════════════════════════════════════════════════════════
export function WinRateByRegime({ C }) {
  const { status, loading } = useMarkovStatus();
  const stats = status?.regimeStats || {};
  const hasData = Object.keys(stats).length > 0;

  return (
    <div>
      <div style={{ fontSize: 8, color: C?.muted, fontFamily: 'JetBrains Mono', letterSpacing: 1, marginBottom: 10 }}>
        WIN RATE BY MARKET REGIME
      </div>

      {loading ? (
        <div style={{ color: C?.muted, fontSize: 10 }}>Loading...</div>
      ) : !hasData ? (
        <div style={{ color: C?.muted, fontSize: 10, fontFamily: 'JetBrains Mono', padding: '10px 0' }}>
          No regime data yet — builds after first trades
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {['TRENDING', 'CHOPPY', 'VOLATILE'].map(label => {
            const s = stats[label];
            if (!s) return (
              <div key={label} style={{ opacity: 0.4 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                  <span style={{ fontSize: 9, fontFamily: 'JetBrains Mono', color: REGIME_COLORS[label]?.text }}>{label}</span>
                  <span style={{ fontSize: 9, fontFamily: 'JetBrains Mono', color: C?.muted }}>NO DATA</span>
                </div>
                <div style={{ height: 6, background: C?.border, borderRadius: 3 }} />
              </div>
            );

            const wr   = parseFloat(s.winRate);
            const col  = REGIME_COLORS[label]?.text;
            const barW = `${Math.min(100, wr)}%`;

            return (
              <div key={label}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                  <span style={{ fontSize: 9, fontFamily: 'JetBrains Mono', color: col }}>
                    {label}
                  </span>
                  <span style={{ fontSize: 9, fontFamily: 'JetBrains Mono', color: C?.text, fontWeight: 700 }}>
                    {s.winRate}% <span style={{ color: C?.muted, fontWeight: 400 }}>({s.total} trades, avg ${s.avgPnl})</span>
                  </span>
                </div>
                <div style={{ height: 6, background: C?.border, borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', width: barW, borderRadius: 3,
                    background: wr >= 75 ? (C?.green) : wr >= 60 ? (C?.yellow) : (C?.red),
                    transition: 'width 0.5s ease',
                  }} />
                </div>
                {/* 75% target line indicator */}
                <div style={{ position: 'relative', height: 3 }}>
                  <div style={{
                    position: 'absolute', left: '75%', top: 0,
                    width: 1, height: 6, background: C?.muted, opacity: 0.5,
                    transform: 'translateY(-4px)',
                  }} />
                </div>
              </div>
            );
          })}
          <div style={{ fontSize: 8, color: C?.muted, fontFamily: 'JetBrains Mono', marginTop: 2 }}>
            ▏ 75% target line
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// 7. ALERT PANEL — last 10 bot log lines
// Usage: <AlertPanel C={C} />
// ═══════════════════════════════════════════════════════════════════════════
export function AlertPanel({ C }) {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    fetch('/api/markov/alerts')
      .then(r => r.json())
      .then(d => { setAlerts(d.alerts || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const getColor = (line) => {
    if (line.includes('ENTRY'))  return C?.green  || '#00c853';
    if (line.includes('EXIT WIN'))  return C?.green;
    if (line.includes('EXIT LOSS')) return C?.red;
    if (line.includes('PAUSED') || line.includes('STOP')) return C?.red || '#ff3d00';
    if (line.includes('WARN'))   return C?.yellow || '#ffd600';
    if (line.includes('REGIME')) return C?.accent || '#f5f5f5';
    return C?.muted || '#757575';
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ fontSize: 8, color: C?.muted, fontFamily: 'JetBrains Mono', letterSpacing: 1 }}>BOT ACTIVITY LOG</div>
        <button onClick={load} style={{
          padding: '2px 6px', borderRadius: 3, fontSize: 8, cursor: 'pointer',
          fontFamily: 'JetBrains Mono', border: `1px solid ${C?.border}`,
          background: 'transparent', color: C?.muted,
        }}>REFRESH</button>
      </div>

      <div style={{
        background: C?.dim || '#0a0a0a',
        border: `1px solid ${C?.border}`,
        borderRadius: 4, padding: 8,
        maxHeight: 160, overflowY: 'auto',
        fontFamily: 'JetBrains Mono', fontSize: 9,
      }}>
        {loading ? (
          <div style={{ color: C?.muted }}>Loading...</div>
        ) : alerts.length === 0 ? (
          <div style={{ color: C?.muted }}>No recent activity — bot may be stopped</div>
        ) : (
          alerts.map((line, i) => (
            <div key={i} style={{ color: getColor(line), marginBottom: 2, lineHeight: 1.4 }}>
              {line}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// 8. MARKOV STATUS BAR — top bar combining regime + P&L + kill switch
// This is the main component to add to your header area
// Usage: <MarkovStatusBar C={C} />
// ═══════════════════════════════════════════════════════════════════════════
export function MarkovStatusBar({ C }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      padding: '8px 16px',
      background: C?.panel || '#1a1a1a',
      border: `1px solid ${C?.border || '#2a2a2a'}`,
      borderRadius: 8,
      flexWrap: 'wrap',
    }}>
      {/* Regime */}
      <RegimeBadge C={C} />

      {/* Divider */}
      <div style={{ width: 1, height: 28, background: C?.border }} />

      {/* P&L */}
      <SessionPnL C={C} />

      {/* Divider */}
      <div style={{ width: 1, height: 28, background: C?.border }} />

      {/* Sequence risk */}
      <SequenceRiskBar C={C} />

      {/* Spacer */}
      <div style={{ flex: 1 }} />

      {/* Kill switch */}
      <KillSwitch C={C} />
    </div>
  );
}
